import { createContext, useContext, useMemo } from 'react';
import { useAuth } from './authContext';

/**
 * The submitter a form is being filled in for, when it is not the logged-in user.
 *
 * The Submissions folder's submit modal provides it when a "Sent" row is opened
 * (the 'Ability to view and submit forms with status "Sent"' permission): the
 * submission is recorded as that submitter's own, so everything that stamps "who
 * is filling this in" -- the submitters field, a "current user" table column --
 * has to name them rather than whoever is at the keyboard. Its presence is also
 * what tells the role hooks this is the submitter's view, off the send-form route.
 *
 * Shape (from the backend's `on_behalf_of`):
 * { submission_notification_id, submitter_type, user_id, first_name, last_name, position }
 */
const OnBehalfContext = createContext(null);

export const OnBehalfProvider = ({ submitter, children }) => {
  return <OnBehalfContext.Provider value={submitter ?? null}>{children}</OnBehalfContext.Provider>;
};

export const useOnBehalfSubmitter = () => useContext(OnBehalfContext);

/**
 * Whom a form element should treat as the person filling the form in: the
 * on-behalf submitter when there is one, the logged-in user otherwise. Returned
 * in the shape of the auth user (`first_name`, `last_name`, `position.name`, `id`),
 * so callers do not have to know which of the two they got.
 */
export const useFormUser = () => {
  const { user } = useAuth();
  const onBehalfOf = useOnBehalfSubmitter();

  return useMemo(() => {
    if (!onBehalfOf) {
      return user;
    }

    return {
      id: onBehalfOf.user_id ?? null,
      first_name: onBehalfOf.first_name,
      last_name: onBehalfOf.last_name,
      position: onBehalfOf.position ? { name: onBehalfOf.position } : null,
    };
  }, [user, onBehalfOf]);
};
